import React, { ComponentType, Suspense, useReducer } from 'react';
import { useLocation } from 'react-router-dom';
import { LoadingSpinner } from '@/components/ui';
import { ErrorBoundary } from '@/components/error/ErrorBoundaryHierarchy';
import { isStaleChunkError } from '@/lib/stale-chunk-error';

const RELOAD_WINDOW_MS = 60_000;
const MAX_RELOADS_PER_WINDOW = 3;
const RELOAD_STATE_KEY = 'chunk-reload-state';

/**
 * Wraps a dynamic import to handle stale chunk errors after deployment.
 * When Vite rebuilds, old chunk hashes become invalid. If a cached page
 * references a stale chunk, we reload the page to get the new HTML.
 *
 * Up to 3 auto-reloads per rolling 60s window: a tab kept open across several
 * deploys gets a fresh reload each time and back-to-back reloads self-heal
 * CDN propagation lag, while the cap prevents an endless reload loop when a
 * cached stale index.html keeps failing. Hitting the cap propagates the error
 * (so it reaches the boundary and Sentry) and resets the guard, giving the
 * user's next manual retry a fresh reload budget.
 */
function retryImport(
  importFn: () => Promise<{ default: ComponentType<unknown> }>
): Promise<{ default: ComponentType<unknown> }> {
  return importFn().catch((error: unknown) => {
    const message = error instanceof Error ? error.message : String(error);

    if (isStaleChunkError(message)) {
      let lastReloadAt = 0;
      let reloadCount = 0;
      try {
        const parsed = JSON.parse(sessionStorage.getItem(RELOAD_STATE_KEY) ?? 'null');
        if (parsed && typeof parsed === 'object') {
          lastReloadAt = Number(parsed.at);
          reloadCount = Number(parsed.count) || 0;
        }
      } catch {
        // absent or malformed — treat as a fresh window
      }

      const elapsed = Date.now() - lastReloadAt;
      // NaN or a backwards clock step fails open into a fresh window
      const inWindow = elapsed >= 0 && elapsed < RELOAD_WINDOW_MS;
      const recentReloads = inWindow ? reloadCount : 0;

      try {
        if (recentReloads < MAX_RELOADS_PER_WINDOW) {
          sessionStorage.setItem(
            RELOAD_STATE_KEY,
            JSON.stringify({ count: recentReloads + 1, at: Date.now() })
          );
          window.location.reload();
          // Return a never-resolving promise to prevent React from rendering an error
          return new Promise<never>(() => {});
        }
        // Cap hit: clear the guard before propagating, so the next
        // user-initiated retry (Try Again / navigation) can reload again
        // instead of being locked out for the rest of the window.
        sessionStorage.removeItem(RELOAD_STATE_KEY);
      } catch {
        // Storage denied (private browsing / quota): we can't rate-limit
        // reloads, so don't risk a loop — propagate the original chunk error.
      }
    }

    throw error;
  });
}

// Helper function to create lazy routes with consistent loading states
export function createLazyRoute(
  importFn: () => Promise<{ default: ComponentType<unknown> }>,
  options?: {
    fallback?: React.ReactNode;
    errorFallback?: React.ReactNode;
  }
) {
  const makeLazy = () => React.lazy(() => retryImport(importFn));
  // Shared across mounts so the happy path never re-suspends; only rebuilt
  // when a chunk load fails (see onError below).
  let LazyComponent = makeLazy();

  return function LazyRouteWrapper(props: Record<string, unknown>) {
    // Contain page crashes to the route: without this, any page error bubbles
    // to the root boundary and takes down the whole app shell. resetKey on
    // location.key (new on every navigation, including query/hash-only ones)
    // lets navigation recover from a crashed page without a full reload.
    const location = useLocation();
    const [, forceRender] = useReducer((n: number) => n + 1, 0);
    return (
      <ErrorBoundary
        resetKey={location.key}
        fallback={options?.errorFallback}
        onError={(error) => {
          // React.lazy caches a loader rejection for the component's lifetime,
          // so after a failed chunk load every boundary reset would re-throw
          // the stale rejection. Rebuild the lazy component (render crashes
          // don't poison it, so only for chunk errors) and re-render the
          // wrapper so the boundary's children reference the rebuilt component
          // — that's what makes "Try Again" and navigation actually retry the
          // import.
          if (isStaleChunkError(String(error?.message ?? ''))) {
            LazyComponent = makeLazy();
            forceRender();
          }
        }}
      >
        <Suspense fallback={options?.fallback || <LoadingSpinner />}>
          <LazyComponent {...props} />
        </Suspense>
      </ErrorBoundary>
    );
  };
}

// Preload utility for better UX
export function preloadRoute(importFn: () => Promise<{ default: ComponentType<unknown> }>) {
  importFn().catch(() => {
    // Silently ignore preload failures
  });
}
